import { t } from '@trimble/i18n';
import type { ModusWcTextInputCustomEvent } from '@trimble-oss/moduswebcomponents';
import {
  Component,
  Show,
  batch,
  createEffect,
  createSignal,
  createUniqueId,
} from 'solid-js';
import { readModusInputChangeValue } from '@trimble/utilities';
import { IconButton, BtnClasses } from '@trimble/modus-ui';

export interface ModusEditableTitleProps {
  label: string;
  handleEditLabelSave(label: string | undefined): void;
  hideTitleEdit?: boolean;
  disableEdit?: boolean;
  disabledReason?: string;
}

export const ModusEditableTitle: Component<ModusEditableTitleProps> = (
  props
) => {
  const editLabelInputId = createUniqueId();
  const [isEditingTitle, setIsEditingTitle] = createSignal(false);
  const [editingLabel, setEditingLabel] = createSignal<string>();
  let inputRef: HTMLModusWcTextInputElement | undefined;

  createEffect(() => {
    if (!isEditingTitle()) return;

    queueMicrotask(() => {
      const input = inputRef?.querySelector('input');
      input?.focus();
      input?.select();
    });
  });

  const handleEditButtonClick = (): void => {
    if (props.disableEdit) return;
    batch(() => {
      setEditingLabel(props.label);
      setIsEditingTitle(true);
    });
  };

  const handleLabelChange = (
    event: ModusWcTextInputCustomEvent<InputEvent>
  ): void => {
    setEditingLabel(readModusInputChangeValue(event));
  };

  const handleLabelInputBlur = (): void => {
    setIsEditingTitle(false);
    props.handleEditLabelSave(editingLabel());
  };

  return (
    <Show
      when={!isEditingTitle()}
      fallback={
        <div class="flex flex-1 min-w-0 items-center gap-2 px-2">
          <modus-wc-text-input
            // @ts-expect-error: ref is valid but absent from the generated prop types
            ref={(element: HTMLModusWcTextInputElement) => {
              inputRef = element;
            }}
            class="flex-1 min-w-0"
            input-id={editLabelInputId}
            name={editLabelInputId}
            size="sm"
            custom-class="!mb-0"
            value={editingLabel() ?? ''}
            placeholder={props.label}
            aria-label={t('general.edit')}
            on:inputChange={handleLabelChange}
            on:inputBlur={handleLabelInputBlur}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                (event.composedPath()[0] as HTMLInputElement).blur();
              }
            }}
          />
        </div>
      }
    >
      <div class="flex flex-1 min-w-0 items-center justify-between">
        <div
          data-testid="editable-title-label"
          class="text-lg font-semibold px-2 truncate min-w-0"
          textContent={props.label}
        />

        <Show when={!props.hideTitleEdit}>
          <IconButton
            color={BtnClasses.Tertiary}
            icon="pencil"
            size="icon-medium"
            data-cy="edit-icon"
            clicked={handleEditButtonClick}
            title={
              props.disableEdit
                ? (props.disabledReason ?? '')
                : t('general.edit')
            }
            disabled={props.disableEdit}
          />
        </Show>
      </div>
    </Show>
  );
};
